<template>
  <v-data-table
    :headers="options.headers"
    :items="itemList"
    :item-key="options.rowKey"
    :search="options.search"
    :loading="isLoading"
    :show-expand="showExpand"
    loading-text="Loading... Please wait"
    :footer-props="{'items-per-page-options': options.rowsPerPageItems}"
    :sort-by.sync="options.sortBy"
    :sort-desc.sync="options.sortDesc"
    multi-sort
    dense
    @item-expanded="expandItem"
    single-expand
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title>{{ options.title }}</v-toolbar-title>
        <v-spacer />
        <template v-if="dropdownColumns">
          <span class="mr-3">{{ dropdownColumns.title }}</span>
          <v-autocomplete
            v-model="selectedFilter"
            :items="dropdownColumns.values"
            label
            dense
            style="max-width:250px !important;"
            class="mt-5 mr-3"
          />
        </template>
        <div class="dv-search">
          <v-text-field
            v-model="options.search"
            append-icon="search"
            label="Search"
            single-line
            hide-details
            class="text-field-search"
            @keyup="searchTextChanged"
          />
        </div>
      </v-toolbar>
    </template>
    <template
      v-for="(formattedColumn,index) in options.formattedColumns"
      v-slot:[options.formattedColumns[index].slotId]="{ item }"
    >
      <!-- Short Date -->
      <span
        :key="index"
        v-if="isShortDate(formattedColumn.pipe) && item[formattedColumn.id]"
      >
        {{ item[formattedColumn.id] | _shortDateFilter }}
      </span>
      <!-- Date Time -->
      <span
        :key="index"
        v-if="isDateTime(formattedColumn.pipe) && item[formattedColumn.id]"
      >
        {{ item[formattedColumn.id] | _dateTimeFormatFilter }}
      </span>
      <!-- String Replace -->
      <span
        :key="index"
        v-if="isStringReplace(formattedColumn.pipe) && item[formattedColumn.id]"
      >
        {{ item[formattedColumn.id] | _stringReplaceFilter(formattedColumn.options.replace,formattedColumn.options.replaceBy) }}
      </span>
      <!-- External Link -->
      <v-btn
        :key="index"
        v-if="isExernalLink(formattedColumn.pipe) && item[formattedColumn.id]"
        target="blank"
        text
        :href="formattedColumn.options.url + item[formattedColumn.id]"
        class="pa-0"
      >
        {{ item[formattedColumn.id] }}
      </v-btn>
      <!-- Conditional Icon -->
      <v-tooltip
        v-if="isConditionalIconType(formattedColumn.pipe)"
        bottom
        :key="index"
      >
        <template v-slot:activator="{ on }">
          <v-icon
            small
            v-on="on"
            :color="itemColor(item,formattedColumn,formattedColumn.id)"
          >
            {{ itemIcon(item,formattedColumn,formattedColumn.id) }}
          </v-icon>
        </template>
        <span>{{ itemTooltip(item,formattedColumn,formattedColumn.id) }}</span>
      </v-tooltip>
    </template>
    <template
      v-slot:expanded-item="{ headers }"
      v-if="showExpand"
    >
      <td
        :colspan="headers.length"
        class="pa-4"
      >
        <v-row
          dense
          no-gutters
        >
          <v-col
            v-for="chItem in expandedItems"
            :key="chItem.title"
            cols="4"
          >
            <v-card
              outlined
              tile
            >
              <span class="ml-1 font-weight-black">{{ chItem.title }}:</span>
              {{ chItem.value }}
            </v-card>
          </v-col>
        </v-row>
      </td>
    </template>
    <template v-slot:item.action="{ item }">
      <v-menu
        :close-on-content-click="true"
        offset-y
        v-if="actionsToShow(item).length > 0"
      >
        <template
          v-slot:activator="{ on }"
          :elevation="hover ? 16 : 2"
        >
          <v-btn
            v-on="on"
            fab
            small
            outlined
            class="transparent-border hover-gray"
          >
            <span class="title">...</span>
          </v-btn>
        </template>
        <v-list
          dense
          class="pa-0"
        >
          <v-list-item class="pa-0">
            <v-list-item-content class="pa-0">
              <template v-for="ahandler in actionsToShow(item)">
                <v-btn
                  :key="ahandler.title"
                  text
                  class="pa-0 action-button"
                  @click="handleEvent(ahandler.title,item)"
                >
                  <v-icon
                    v-if="ahandler.icon"
                    left
                    small
                  >
                    {{ ahandler.icon }}
                  </v-icon>
                  {{ ahandler.title }}
                </v-btn>
              </template>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
</template>

<script>
  import {
    FILED_TYPE_SHORT_DATE,
    FILED_TYPE_EXTERNAL_LINK,
    FILED_TYPE_DATE_TIME,
    FILED_TYPE_STRING_REPLACE,
    FILED_TYPE_CONDITIONAL_ICON
  } from "./TableTypes";

  export default {
    name: "DataTable",
    props: {
      title: {
        type: String,
        default: "",
      },
      headers: {
        type: Array,
        default: () => []
      },
      items: {
        type: Array,
        default: () => []
      },
      rowKey: {
        type: String,
        default: "",
      },
      search: {
        type: String,
        default: "",
      },
      loading: {
        type: Boolean,
        default: false,
      },
      rowsPerPageItems: {
        type: Array,
        default: function () {
          return [10, 20, 30, 50];
        },
      },
      formattedColumns: {
        type: Array,
        default: () => []
      },
      actions: {
        type: Function,
        default: null
      },
      showExpand: {
        type: Boolean,
        default: false,
      },
      sortBy: {
        type: Array,
        default: () => []
      },
      sortDesc: {
        type: Array,
        default: () => []
      },
      expandedItems: {
        type: Array,
        default: () => []
      },
      dropdownColumns: {
        type: Object,
        default: () => null
      }
    },
    beforeMount() {
      this.setOptions();
      if (this.showExpand) {
        this.headers.unshift({text: "", value: "data-table-expand"});
      }
      if (!this.isEmptyObject(this.actions)) {
        this.headers.push({text: "Actions", value: "action", sortable: false});
      }
    },
    mounted() {
      if (this.dropdownColumns) {
        if (this.dropdownColumns.defaultValue)
          this.selectedFilter = this.dropdownColumns.defaultValue;
      }
    },
    data: () => {
      return {
        selectedFilter: "",
        _showSelect: true,
        options: {
          title: "",
          headers: [],
          rowKey: "id",
          search: "",
          rowsPerPageItems: [10, 20, 30, 50],
          formattedColumns: [],
          sortBy: [],
          sortDesc: []
        }
      };
    },
    filters: {
      _shortDateFilter(date) {
        if (date) {
          try {
            let dateValue = new Date(date);
            return dateValue.toLocaleDateString();
          } catch (error) {
            console.error(error);
          }
        }
        return "";
      },
      _dateTimeFormatFilter(date) {
        if (date) {
          try {
            let dateValue = new Date(date);
            return (
              dateValue.toLocaleDateString() +
              " " +
              dateValue.toLocaleTimeString()
            );
          } catch (error) {
            console.error(error);
          }
        }
        return "";
      },
      _stringReplaceFilter(value, source, replaceBy) {
        return value ? value.replace(source, replaceBy) : value;
      }
    },
    computed: {
      itemColor() {
        return (item, filter, id) => {
          if (item[id]) {
            return filter.options[item[id].toLowerCase()].color;
          } else {
            return filter.options["empty"] ? filter.options["empty"].color : "";
          }
        };
      },
      itemIcon() {
        return (item, filter, id) => {
          if (item[id]) {
            return filter.options[item[id].toLowerCase()].icon;
          } else {
            return filter.options["empty"] ? filter.options["empty"].icon : "";
          }
        };
      },
      itemTooltip() {
        return (item, filter, id) => {
          if (item[id]) {
            return filter.options[item[id].toLowerCase()].tooltip;
          } else {
            return filter.options["empty"] ? filter.options["empty"].tooltip : "";
          }
        };
      },
      itemList() {
        return this.selectedFilter === "All" || this.selectedFilter === ""
          ? this.items
          : this.items.filter(item =>
            item[this.dropdownColumns.fieldname]
              ? item[this.dropdownColumns.fieldname].toLowerCase() ==
              this.selectedFilter.toLowerCase()
              : false
          );
      },
      dropdownColumnsValues() {
        return this.dropdownColumns.values;
      },
      showSelect() {
        return (
          this.isEnabled("header.data-table-select") ||
          this.isEnabled("item.data-table-select")
        );
      },
      isLoading() {
        return this.loading;
      }
    },
    methods: {
      expandItem(expand) {
        if (expand.value) {
          this.$emit("emitEvent", {key: "rowExpanded", value: expand.item});
        }
      },
      actionsToShow(item) {
        let actionItems = [];
        try {
          if (typeof this.actions === "function") {
            actionItems = [...this.actions(item)];
          } else {
            if (this.actions) {
              actionItems = this.actions;
            }
          }
        } catch (error) {
          console.error(error);
        }
        return actionItems;
      },
      handleEvent(event, item) {
        this.$emit("emitEvent", {key: event, value: item[this.options.rowKey]});
      },
      searchTextChanged() {
        this.$emit("emitEvent", {
          key: "searchText",
          value: this.options.search
        });
      },
      isExernalLink(item) {
        return item === FILED_TYPE_EXTERNAL_LINK;
      },
      isConditionalIconType(item) {
        return item === FILED_TYPE_CONDITIONAL_ICON;
      },
      isShortDate(item) {
        return item === FILED_TYPE_SHORT_DATE;
      },
      isStringReplace(item) {
        return item === FILED_TYPE_STRING_REPLACE;
      },
      isDateTime(item) {
        return item === FILED_TYPE_DATE_TIME;
      },
      isEmptyObject(value) {
        return !value || value === "" || value.length < 1;
      },
      setOptions() {
        this.options.title = this.isEmptyObject(this.title)
          ? this.options.title
          : this.title;
        this.options.headers = this.isEmptyObject(this.headers)
          ? this.options.headers
          : this.headers;
        this.options.rowKey = this.isEmptyObject(this.rowKey)
          ? this.options.rowKey
          : this.rowKey;
        this.options.search = this.isEmptyObject(this.search)
          ? ''
          : this.search;

        this.options.rowsPerPageItems = this.isEmptyObject(this.rowsPerPageItems)
          ? this.options.rowsPerPageItems
          : this.rowsPerPageItems;

        this.options.formattedColumns = this.isEmptyObject(this.formattedColumns)
          ? this.options.formattedColumns
          : this.formattedColumns;
        this.options.formattedColumns.map(item => (item.slotId = "item." + item.id));

        this.options.sortBy = this.isEmptyObject(this.sortBy)
          ? this.options.sortBy
          : this.sortBy;
        this.options.sortDesc = this.isEmptyObject(this.sortDesc)
          ? this.options.sortDesc
          : this.sortDesc;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .dv-search {
    width: 300px;
  }

  .text-field-search {
    width: 100%;
  }

  .transparent-border {
    border-color: transparent;
  }

  .hover-gray:hover {
    background-color: #041e42;
    color: white;
  }

  .action-button {
    font-size: 12px;
    height: 15px;
    width: 50px !important;
    min-width: 10px !important;
    border-bottom: 0.91px solid;
    justify-content: left !important;
    padding-left: 17px !important;
  }
</style>
